import { add, dist, dot, norm, scale, sub } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, MoveKind, hv, setHv } from "../../sim/components.ts";
import { aim, applyDamage, applyStatus, explode, hitscan, isEnemy, moveProjectile, playersInRadius, spawnProjectile } from "../../sim/combat.ts";
import { addOverhealth } from "../../sim/combat.ts";
import { beginTargeting, endTargeting, targetConfirmed, targetPoint } from "../../sim/targeting.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, contactEnemies, ownerAlive, pull, ready, secs, startMove, trigger, useUlt, wishDir } from "../kitlib.ts";

export const V = { hookCharge: 0, cullTicks: 1, ultRecast: 2, fistHit: 3, ultPhase: 4 } as const;
export const PK = { Round: pk(HeroId.WinterShoulder, 0), Hook: pk(HeroId.WinterShoulder, 1) } as const;
export const FX = { Round: fxk(HeroId.WinterShoulder, 0), Arm: fxk(HeroId.WinterShoulder, 1), Ult: fxk(HeroId.WinterShoulder, 2) } as const;
const FIST = MoveKind.Hero + 1;
const KRAKEN_RISE = MoveKind.Hero + 2;
const KRAKEN_DASH = MoveKind.Hero + 3;
const KRAKEN_TARGET_RANGE = 35;
const KRAKEN_HANG_TIMEOUT = 3.5;
const KRAKEN_RISE_TICKS = 30;
const KRAKEN_RISE_SPEED = 14;
const KRAKEN_DASH_SPEED = 60;

const def: HeroDef = {
  id: HeroId.WinterShoulder,
  slug: "winter-shoulder",
  name: "Winter Shoulder",
  role: Role.Damage,
  color: "#6c7a89",
  blurb: "Combo duelist with explosive sidearm shots and a bionic shoulder for hooks, punches and executions.",
  health: 275,
  armor: 0,
  shields: 0,
  radius: 0.45,
  height: 1.9,
  headRadius: 0.22,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 3,
  reloadTime: 1.35,
  ultCost: 3100,
  abilities: {
    [Slot.Primary]: { name: "Red-Star Sidearm", key: "LMB", description: "Fire explosive rounds that splash behind the hit.", cooldown: 0 },
    [Slot.Secondary]: { name: "Bionic Crook", key: "RMB (hold)", description: "Charge and launch a hook that pulls the first target hit.", cooldown: 15 },
    [Slot.Ability1]: { name: "Trooper's Shoulder", key: "Shift", description: "Dash forward, seize enemies, then launch them.", cooldown: 10 },
    [Slot.Ability2]: { name: "Tainted Current", key: "E", description: "Punch out piercing electricity that damages and slows.", cooldown: 6, charges: 2 },
    [Slot.Ultimate]: { name: "Kraken Shoulder", key: "Q", description: "Leap high with a 5m slam reticle; press LMB to dash down, mark enemies, and refresh on culls.", cooldown: 0 },
  },
  passive: { name: "Ceaseless Chamber", description: "Bionic arm abilities reload the sidearm and grant 40 or 50 temporary bonus health." },
};

function armBonus(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, amount: number) {
  sim.c.Weapon.ammo[eid] = sim.hero(eid).ammo;
  sim.c.Weapon.reload[eid] = 0;
  addOverhealth(sim, eid, amount, secs(5), eid);
}

function ultSlam(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, pos = sim.posOf(eid)) {
  const p = add(pos, { x: 0, y: 0.7, z: 0 });
  sim.emit({ type: "explosion", pos: p, radius: 5, kind: FX.Ult });
  for (const { eid: t } of playersInRadius(sim, p, 5, (e) => isEnemy(sim, eid, e))) {
    applyDamage(sim, t, 70, eid, { pos: p });
    applyStatus(sim, t, "marked", 5, { source: eid, by: eid });
  }
  setHv(sim.c, eid, V.cullTicks, secs(5));
  setHv(sim.c, eid, V.ultPhase, 0);
  sim.c.Ult.active[eid] = 0;
  endTargeting(sim, eid);
}

function abortKraken(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  setHv(sim.c, eid, V.ultPhase, 0);
  sim.c.Ult.active[eid] = 0;
  sim.c.Move.ticks[eid] = 0;
  endTargeting(sim, eid);
}

function beginKraken(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  const c = sim.c;
  setHv(c, eid, V.ultPhase, 1);
  c.Ult.active[eid] = secs(KRAKEN_HANG_TIMEOUT + 1.2);
  beginTargeting(sim, eid, Slot.Ultimate);
  action(sim, eid, ActionKind.Ult, 18);
  armBonus(sim, eid, 50);
  startMove(sim, eid, KRAKEN_RISE, KRAKEN_RISE_TICKS, KRAKEN_RISE_SPEED, { x: 0, y: KRAKEN_RISE_SPEED, z: 0 });
}

function dashKraken(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  const t = targetPoint(sim, eid, Slot.Ultimate).pos;
  const d = dist(sim.posOf(eid), t);
  const ticks = Math.max(8, Math.min(54, Math.ceil((d / KRAKEN_DASH_SPEED) * 60) + 4));
  setHv(sim.c, eid, V.ultPhase, 2);
  action(sim, eid, ActionKind.Ult, 14);
  startMove(sim, eid, KRAKEN_DASH, ticks, KRAKEN_DASH_SPEED, t);
}

const module: HeroModule = {
  def,
  kit: {
    init() {},
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      if (hv(c, eid, V.cullTicks) > 0) setHv(c, eid, V.cullTicks, hv(c, eid, V.cullTicks) - 1);
      if (hv(c, eid, V.ultRecast) > 0) setHv(c, eid, V.ultRecast, hv(c, eid, V.ultRecast) - 1);
      if (hv(c, eid, V.ultPhase) > 0) {
        if (!canAct) abortKraken(sim, eid);
        else {
          if (hv(c, eid, V.ultPhase) === 1 && targetConfirmed(sim, eid, Slot.Ultimate)) dashKraken(sim, eid);
          if (hv(c, eid, V.ultPhase) === 1) c.Vel.y[eid] = Math.max(c.Vel.y[eid]!, -0.8);
        }
        return;
      }

      commonInput(sim, eid, input, pressed);
      if (!canAct) return;

      if (input.buttons & Btn.Secondary) setHv(c, eid, V.hookCharge, Math.min(1, hv(c, eid, V.hookCharge) + 1 / 60));
      else if (hv(c, eid, V.hookCharge) > 0) {
        if (ready(sim, eid, Slot.Secondary)) {
          consume(sim, eid, Slot.Secondary);
          action(sim, eid, ActionKind.Cast, 14);
          armBonus(sim, eid, 40);
          const a = aim(sim, eid);
          const charge = Math.max(0.5, hv(c, eid, V.hookCharge));
          const p = spawnProjectile(sim, eid, { kind: PK.Hook, origin: add(a.origin, scale(a.dir, 0.5)), vel: scale(a.dir, 80), radius: 0.2, seconds: charge * 0.25, seq: input.seq });
          if (p !== undefined) c.Projectile.bounces[p] = Math.round(10 + charge * 10);
        }
        setHv(c, eid, V.hookCharge, 0);
      }

      if ((useUlt(sim, eid, input, pressed) || ((pressed & Btn.Ultimate) && hv(c, eid, V.ultRecast) > 0))) {
        setHv(c, eid, V.ultRecast, 0);
        beginKraken(sim, eid);
      }
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        action(sim, eid, ActionKind.Melee, 16);
        armBonus(sim, eid, 50);
        setHv(c, eid, V.fistHit, 0);
        const d = wishDir(input);
        startMove(sim, eid, FIST, 18, 30, { x: d.x * 30, y: 0, z: d.z * 30 });
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 10);
        armBonus(sim, eid, 40);
        const a = aim(sim, eid);
        hitscan(sim, eid, a.origin, a.dir, { range: 35, damage: 55, fxKind: FX.Arm, pierce: 6 });
        for (const { eid: t } of playersInRadius(sim, add(a.origin, scale(a.dir, 16)), 18, (e) => isEnemy(sim, eid, e), true)) {
          const to = sub(add(sim.posOf(t), { x: 0, y: 1, z: 0 }), a.origin);
          const along = dot(norm(to), a.dir);
          if (along > 0.94) {
            applyStatus(sim, t, "slow", 2, { slowPct: 0.25, source: eid });
            if (!c.Player.grounded[t]) applyStatus(sim, t, "knockdown", 0.5, { source: eid });
          }
        }
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        c.Weapon.fireCd[eid] = secs(0.4);
        action(sim, eid, ActionKind.Fire, 8);
        const a = aim(sim, eid);
        const p = spawnProjectile(sim, eid, { kind: PK.Round, origin: add(a.origin, scale(a.dir, 0.5)), vel: scale(a.dir, 180), radius: 0.1, seconds: 1, seq: input.seq });
        if (p !== undefined) c.Projectile.bounces[p] = 70;
      }
    },
    scripted(sim, eid, _input, kind) {
      const c = sim.c;
      if (kind === KRAKEN_RISE) {
        c.Vel.x[eid] = 0;
        c.Vel.z[eid] = 0;
        c.Vel.y[eid] = c.Move.ticks[eid] === 1 ? 0 : KRAKEN_RISE_SPEED;
        if (c.Move.ticks[eid] === 1) c.Move.ticks[eid] = 0;
        return;
      }
      if (kind === KRAKEN_DASH) {
        const target = { x: c.Move.x[eid]!, y: c.Move.y[eid]!, z: c.Move.z[eid]! };
        const p = sim.posOf(eid);
        const d = dist(p, target);
        if (d < 1.2 || c.Move.blocked[eid] || c.Move.ticks[eid] === 1) {
          // Slam where he came down: at the target, or short of it if something was in the way.
          ultSlam(sim, eid, d < 1.2 ? target : p);
          c.Move.ticks[eid] = 0;
          c.Vel.x[eid] = 0;
          c.Vel.y[eid] = Math.min(0, c.Vel.y[eid]!);
          c.Vel.z[eid] = 0;
          return;
        }
        const dir = norm(sub(target, p));
        c.Vel.x[eid] = dir.x * KRAKEN_DASH_SPEED;
        c.Vel.y[eid] = dir.y * KRAKEN_DASH_SPEED;
        c.Vel.z[eid] = dir.z * KRAKEN_DASH_SPEED;
        return;
      }
      if (kind !== FIST) return;
      c.Vel.x[eid] = c.Move.x[eid]!;
      c.Vel.z[eid] = c.Move.z[eid]!;
      c.Vel.y[eid] = 0;
      contactEnemies(sim, eid, 2.4, (t) => {
        if (hv(c, eid, V.fistHit) > 0) return;
        setHv(c, eid, V.fistHit, 1);
        applyDamage(sim, t, 25, eid);
      });
      if (c.Move.ticks[eid] === 1 || c.Move.blocked[eid]) {
        for (const { eid: t } of playersInRadius(sim, add(sim.posOf(eid), { x: 0, y: 1, z: 0 }), 4.5, (e) => isEnemy(sim, eid, e), false)) {
          applyDamage(sim, t, 65, eid);
          c.Vel.y[t] = Math.max(c.Vel.y[t]!, 7);
          c.Player.grounded[t] = 0;
        }
      }
    },
    dealtDamage(sim, attacker, target) {
      if (hv(sim.c, attacker, V.cullTicks) <= 0 || sim.c.Status.marked[target] <= 0 || sim.c.Status.markedBy[target] !== attacker + 1) return;
      const max = sim.c.Health.maxHp[target]! + sim.c.Health.maxArmor[target]! + sim.c.Health.maxShield[target]!;
      const hp = sim.c.Health.hp[target]! + sim.c.Health.armor[target]! + sim.c.Health.shield[target]!;
      if (hp > 0 && hp <= max * 0.2) {
        applyDamage(sim, target, 9999, attacker, { quiet: true });
        setHv(sim.c, attacker, V.ultRecast, secs(8));
      }
    },
  },
  projectiles: {
    [PK.Round]: {
      visual: { color: "#c4d1df", size: 0.13, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p);
        if (!hit) {
          if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
          return;
        }
        if (ownerAlive(sim, owner)) {
          if (hit.target !== undefined) applyDamage(sim, hit.target, 80, owner, { crit: hit.head, pos: hit.pos });
          explode(sim, owner, hit.pos, { radius: 5, damage: 45, minDamage: 45, fxKind: FX.Round, skip: hit.target });
        }
        sim.destroyEntity(p);
      },
    },
    [PK.Hook]: {
      visual: { color: "#aeb8c5", size: 0.18, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p);
        if (!hit) {
          if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
          return;
        }
        if (hit.target !== undefined && ownerAlive(sim, owner)) {
          applyDamage(sim, hit.target, 15, owner, { pos: hit.pos });
          pull(sim, hit.target, add(sim.posOf(owner), { x: 0, y: 1, z: 0 }), { speed: 22, seconds: 0.45, source: owner });
          for (const { eid: t } of playersInRadius(sim, hit.pos, 4.5, (e) => isEnemy(sim, owner, e) && e !== hit.target, false)) {
            applyDamage(sim, t, 20, owner, { pos: hit.pos });
            pull(sim, t, add(sim.posOf(owner), { x: 0, y: 1, z: 0 }), { speed: 18, seconds: 0.35, source: owner });
          }
        }
        sim.emit({ type: "impact", pos: hit.pos, kind: FX.Arm });
        sim.destroyEntity(p);
      },
    },
  },
  fx: {
    [FX.Round]: { color: "#c4d1df" },
    [FX.Arm]: { color: "#9fb4c8" },
    [FX.Ult]: { color: "#d7ecff" },
  },
  targets: {
    [Slot.Ultimate]: { range: KRAKEN_TARGET_RANGE, radius: 5, start: "kit", timeout: KRAKEN_HANG_TIMEOUT, verb: "Slam" },
  },
};

export default module;
